import React, { useState, useCallback, useMemo, useRef } from "react";
import { View, Pressable, StyleSheet } from "react-native";
import { useTheme } from "@/hooks/useTheme";
import { useToolTip } from "@/hooks/useToolTip";
import { ThemedText } from "@/components/ThemedText";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { TableRowProps } from "./types";
import { TableCell } from "./TableCell";

interface RowActionButtonProps {
  icon: keyof typeof MaterialCommunityIcons.glyphMap;
  onPress: () => void;
  color?: string;
  disabledColor?: string;
  label?: string;
  disabled?: boolean;
}

function RowActionButton({ icon, onPress, color, disabledColor, label, disabled }: RowActionButtonProps) {
  const { colors } = useTheme();
  const toolTip = useToolTip();
  const ref = useRef<View>(null);
  const isHovering = useRef(false);

  const handlePointerEnter = useCallback(() => {
    if (!label) return;
    isHovering.current = true;
    ref.current?.measureInWindow((x, y, width) => {
      if (!isHovering.current) return;
      const tooltipWidth = label.length * 7 + 16;
      const tooltipHeight = 28;
      toolTip.setLocation({
        width: tooltipWidth,
        height: tooltipHeight,
        x: x + width - tooltipWidth,
        y: y - tooltipHeight - 6,
      });
      toolTip.setToolTipContent(
        <View
          style={{
            backgroundColor: colors.background,
            paddingHorizontal: 8,
            paddingVertical: 4,
            borderRadius: 8,
            borderWidth: StyleSheet.hairlineWidth,
            borderColor: colors.border,
          }}
        >
          <ThemedText style={{ fontSize: 12 }} numberOfLines={1}>
            {label}
          </ThemedText>
        </View>,
      );
      toolTip.setVisible(true);
    });
  }, [label, colors.background, colors.border, toolTip]);

  const handlePointerLeave = useCallback(() => {
    isHovering.current = false;
    toolTip.setVisible(false);
  }, [toolTip]);

  const iconColor = disabled ? disabledColor || colors.textSecondary : color || colors.primary;

  const handlePress = useCallback(() => {
    // Close tooltip on press
    isHovering.current = false;
    toolTip.setVisible(false);
    onPress();
  }, [onPress, toolTip]);

  return (
    <Pressable
      ref={ref}
      onPress={disabled ? undefined : handlePress}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      style={({ pressed }) => [styles.actionButton, pressed && !disabled && { opacity: 0.5 }]}
      disabled={disabled}
    >
      <MaterialCommunityIcons name={icon} size={20} color={iconColor} style={disabled ? { opacity: 0.4 } : undefined} />
    </Pressable>
  );
}

export function TableRow<T>({
  row,
  index,
  columns,
  onPress,
  rowActions,
  minActionSlots,
  onEdit,
  canEdit,
  onDelete,
  canDelete,
  style,
  selectable,
  isSelected,
  onToggleSelect,
  showTooltipWhileEditing,
}: TableRowProps<T>) {
  const { colors } = useTheme();

  // Calculate min width for actions cell based on minActionSlots
  // Each action: 28px (icon 20 + padding 8), gap: 4px, cell padding: 16px
  const actionsCellMinWidth = minActionSlots ? 16 + 28 * minActionSlots + 4 * Math.max(0, minActionSlots - 1) : 80;
  const [isEditing, setIsEditing] = useState(false);
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});

  // Check if any column is editable (either via table onEdit or column onEdit)
  const hasEditableColumns = useMemo(
    () => columns.some((col) => col.editable && (onEdit || col.editable.onEdit)),
    [columns, onEdit],
  );

  // Handle entering edit mode
  const handleStartEdit = useCallback(() => {
    // Initialize edited values with current values
    const initialValues: Record<string, string> = {};
    columns.forEach((col) => {
      if (col.editable) {
        const value = col.accessor ? col.accessor(row) : (row as any)[col.key];
        initialValues[col.key] = value ?? "";
      }
    });
    setEditedValues(initialValues);
    setIsEditing(true);
  }, [columns, row]);

  // Handle cancel - revert and exit edit mode
  const handleCancel = useCallback(() => {
    setEditedValues({});
    setIsEditing(false);
  }, []);

  // Handle save - call onEdit for each changed value
  const handleSave = useCallback(() => {
    Object.entries(editedValues).forEach(([key, newValue]) => {
      const column = columns.find((c) => c.key === key);
      if (column) {
        const originalValue = column.accessor ? column.accessor(row) : (row as any)[key];
        if (newValue !== originalValue) {
          // Priority: column-level onEdit > table-level onEdit
          const editHandler = column.editable?.onEdit || onEdit;
          if (editHandler) {
            editHandler(row, key, newValue);
          }
        }
      }
    });

    setEditedValues({});
    setIsEditing(false);
  }, [editedValues, columns, row, onEdit]);

  // Handle value change for a specific column
  const handleValueChange = useCallback((key: string, newValue: string) => {
    setEditedValues((prev) => ({ ...prev, [key]: newValue }));
  }, []);

  // Determine which actions to show
  const renderActions = () => {
    if (isEditing) {
      // Show save and cancel when editing
      return (
        <View style={[styles.actionsCell, { minWidth: actionsCellMinWidth }]}>
          <RowActionButton
            icon="check"
            onPress={handleSave}
            color={colors.complete?.text || colors.primary}
            label="Save"
          />
          <RowActionButton
            icon="close"
            onPress={handleCancel}
            color={colors.attention?.text || colors.textSecondary}
            label="Cancel"
          />
        </View>
      );
    }

    // Show normal actions + edit/delete buttons when not editing
    // hasEditableColumns already checks for column-level or table-level onEdit
    const showEditAction = hasEditableColumns;
    const showDeleteAction = onDelete;

    return (
      <View style={[styles.actionsCell, { minWidth: actionsCellMinWidth }]}>
        {showEditAction && <RowActionButton icon="pencil" onPress={handleStartEdit} label="Edit" disabled={!canEdit} />}
        {showDeleteAction && (
          <RowActionButton
            icon="trash-can-outline"
            onPress={() => onDelete(row, index)}
            color={colors.attention?.text || colors.red?.text}
            label="Delete"
            disabled={!canDelete}
          />
        )}
        {rowActions?.map((action, actionIndex) => {
          const isHidden = action.hidden?.(row, index);
          if (isHidden) return null;

          return (
            <RowActionButton
              key={actionIndex}
              icon={action.icon}
              onPress={() => action.onPress(row, index)}
              color={action.color}
              label={action.label}
            />
          );
        })}
      </View>
    );
  };

  const content = (
    <View
      style={[
        styles.row,
        { borderBottomColor: colors.border },
        index % 2 === 1 && { backgroundColor: colors.backgroundSecondary },
        isEditing && { backgroundColor: colors.primary + "10" },
        isSelected && { backgroundColor: colors.primary + "15" },
        style,
      ]}
    >
      {selectable && (
        <Pressable style={styles.selectCell} onPress={(e) => onToggleSelect?.((e.nativeEvent as any).shiftKey)}>
          <MaterialCommunityIcons
            name={isSelected ? "checkbox-marked" : "checkbox-blank-outline"}
            size={24}
            color={isSelected ? colors.primary : colors.text}
          />
        </Pressable>
      )}
      {columns.map((column) => {
        const originalValue = column.accessor ? column.accessor(row) : (row as any)[column.key];
        const displayValue = isEditing && column.editable ? (editedValues[column.key] ?? originalValue) : originalValue;

        return (
          <TableCell
            key={column.key}
            value={displayValue}
            column={column}
            row={row}
            index={index}
            isEditing={isEditing}
            onValueChange={column.editable ? (newValue) => handleValueChange(column.key, newValue) : undefined}
            showTooltipWhileEditing={showTooltipWhileEditing}
          />
        );
      })}
      {(rowActions?.length || (hasEditableColumns && onEdit) || onDelete) && renderActions()}
    </View>
  );

  if (onPress && !isEditing) {
    return (
      <Pressable onPress={onPress} style={({ pressed }) => [pressed && { opacity: 0.7 }]}>
        {content}
      </Pressable>
    );
  }

  return content;
}

const styles = StyleSheet.create({
  row: {
    flexDirection: "row",
    borderBottomWidth: StyleSheet.hairlineWidth,
    minHeight: 44,
  },
  selectCell: {
    minWidth: 48,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 8,
  },
  actionsCell: {
    minWidth: 80,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 4,
    paddingHorizontal: 8,
  },
  actionButton: {
    padding: 4,
  },
});
